import { useEffect, useRef, useCallback } from "react";
import { useAuth, useSession, useUser } from "@clerk/clerk-react";
import { getFormName, trackAnalytics } from "../pages/helpers";
import { BUTTON_ROLES, BUTTON_TYPES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { trackPixelEvent } from "src/services/facebookPixel";

export const useClerkEventTracking = () => {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { user, isLoaded: userLoaded } = useUser();

  const prevSignedInRef = useRef(null);
  const prevSessionIdRef = useRef(null);
  const hasTrackedSignUpRef = useRef(false);
  const mutationObserverRef = useRef(null);
  const eventListenersRef = useRef(new Set());
  const inputTimeoutsRef = useRef(new Map());

  useEffect(() => {
    if (!isSignedIn || !user) return;

    const accountAge = Date.now() - new Date(user.createdAt).getTime();
    const isNewUser = accountAge < 60000; // Less than 1 minute old
    const userEmail = user.primaryEmailAddress?.emailAddress;

    if (isNewUser && !hasTrackedSignUpRef.current) {
      // Event 108:
      trackAnalytics(EVENT_NAMES.COMPLETED_SIGNUP, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.SIGNUP_METHOD]: userEmail ? "email" : "oauth",
        [EVENT_PARAMETERS.USER_EMAIL]: userEmail || "",
      });
      trackPixelEvent("CompleteRegistration", {
        method: userEmail ? "email" : "oauth",
      });
      hasTrackedSignUpRef.current = true;
    } else if (!isNewUser && prevSignedInRef.current === false) {
      // Event 107:
      trackAnalytics(EVENT_NAMES.COMPLETED_SIGNIN, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.SIGNIN_METHOD]: session?.factorVerificationAge === 0 ? "password" : "other",
        [EVENT_PARAMETERS.USER_EMAIL]: userEmail || "",
      });
    }

    prevSignedInRef.current = isSignedIn;
    prevSessionIdRef.current = session?.id || null;
  }, [authLoaded, sessionLoaded, userLoaded, isSignedIn, session, user]);

  // Add event listener with cleanup tracking
  const addEventListenerWithCleanup = useCallback((element, event, handler) => {
    element.addEventListener(event, handler);
    const cleanup = () => element.removeEventListener(event, handler);
    eventListenersRef.current.add(cleanup);
    return cleanup;
  }, []);

  // Setup DOM event listeners for Clerk form elements
  const setupClerkFormListeners = useCallback(() => {
    // Continue/Submit button events
    const continueButtons = document.querySelectorAll(
      '.cl-formButtonPrimary, [data-localization-key="signIn.start.actionText"], [data-localization-key="signUp.start.actionText"]',
    );
    continueButtons.forEach((button) => {
      addEventListenerWithCleanup(button, "click", () => {
        var fieldLocation = getFormName();
        const buttonText = button.textContent?.trim() || "Unknown";
        // Event 109:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Clerk Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: buttonText,
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            // [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });

    // Google OAuth button events
    const googleButtons = document.querySelectorAll(
      '.cl-socialButtonsBlockButton[data-provider="google"], .cl-socialButtonsBlockButton:has([alt*="Google"])',
    );
    googleButtons.forEach((button) => {
      addEventListenerWithCleanup(button, "click", () => {
        var fieldLocation = getFormName();

        // Event 116:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Google Auth Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: "Sign in with Google",
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            // [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });

    // Generic social buttons (fallback)
    const socialButtons = document.querySelectorAll(".cl-socialButtonsBlockButton");
    socialButtons.forEach((button) => {
      // Skip if already handled by Google-specific handler
      if (button.getAttribute("data-provider") === "google" || button.querySelector('[alt*="Google"]')) {
        return;
      }

      addEventListenerWithCleanup(button, "click", () => {
        const provider = button.getAttribute("data-provider") || "Unknown";
        var fieldLocation = getFormName();
        // Event 117:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Social Auth Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: `Sign in with ${provider}`,
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            provider_name: provider,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });
  }, [addEventListenerWithCleanup]);

  // Setup MutationObserver to detect when Clerk forms are rendered
  useEffect(() => {
    const eventListeners = eventListenersRef.current;
    const inputTimeouts = inputTimeoutsRef.current;

    const observer = new MutationObserver((mutations) => {
      let shouldSetupListeners = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if Clerk components were added
              if (
                node.classList?.contains("cl-card") ||
                node.classList?.contains("cl-formFieldInput") ||
                node.querySelector?.(".cl-card, .cl-formFieldInput")
              ) {
                shouldSetupListeners = true;
              }
            }
          });
        }
      });

      if (shouldSetupListeners) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(setupClerkFormListeners, 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    mutationObserverRef.current = observer;

    // Initial setup in case forms are already rendered
    setTimeout(setupClerkFormListeners, 100);

    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }

      // Clean up all event listeners
      eventListeners.forEach((cleanup) => cleanup());
      eventListeners.clear();

      // Clear any pending timeouts
      inputTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      inputTimeouts.clear();
    };
  }, [setupClerkFormListeners]);
};
